import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Pause, Play } from "lucide-react";
import { CornerTicks, scrollToId } from "./decor";
import { ASSETS } from "../data/siteData";
import { useLang } from "../i18n";

const MaskedLine = ({ text, cls, delay, big = false, small = false, spaced = false }) => (
  <div className={`overflow-hidden ${spaced ? "mt-6 sm:mt-8" : ""}`}>
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block font-display uppercase leading-[0.9] tracking-wide ${
        big
          ? "text-[19vw] sm:text-[12.5vw] lg:text-[6rem] xl:text-[7.5rem]"
          : small
            ? "text-[11vw] sm:text-[7.7vw] lg:text-[3.6rem] xl:text-[4.5rem]"
            : "text-[16vw] sm:text-[11vw] lg:text-[5.2rem] xl:text-[6.5rem]"
      } ${cls}`}
    >
      {text}
    </motion.span>
  </div>
);

const MaskedImage = ({ src, alt, delay }) => (
  <div className="overflow-hidden">
    <motion.img
      src={src}
      alt={alt}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      className="block h-[19vw] sm:h-[12.5vw] lg:h-[6rem] xl:h-[7.5rem] w-auto mx-auto lg:mx-0 drop-shadow-[0_6px_24px_rgba(220,38,38,0.35)]"
    />
  </div>
);

const TEASER_FADE = 3;

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

const AudioTeaser = ({ id, title, src, start, end, playingId, onToggle, fadeInLabel, fadeOutLabel }) => {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const playing = playingId === id;

  const onTimeUpdate = () => {
    const a = audioRef.current;
    if (!a) return;
    const t = a.currentTime;
    let vol = 1;
    if (t < start + TEASER_FADE) vol = (t - start) / TEASER_FADE;
    if (t > end - TEASER_FADE) vol = (end - t) / TEASER_FADE;
    a.volume = Math.min(1, Math.max(0, vol));
    setProgress(Math.min(1, Math.max(0, (t - start) / (end - start))));
    if (t >= end) {
      a.currentTime = start;
      a.volume = 0;
    }
  };

  if (!playing && audioRef.current && !audioRef.current.paused) audioRef.current.pause();

  const toggle = async () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      onToggle(null);
      return;
    }
    if (a.currentTime < start || a.currentTime >= end) a.currentTime = start;
    try {
      await a.play();
      onToggle(id);
    } catch {
      onToggle(null);
    }
  };

  return (
    <div data-testid={`audio-teaser-${id}`} className="border border-zinc-800 bg-[#121216]/90 backdrop-blur p-3 flex flex-col gap-3">
      <audio ref={audioRef} src={src} preload="metadata" onTimeUpdate={onTimeUpdate} />
      <div className="flex items-center gap-3 min-w-0">
        <button
          data-testid={`audio-teaser-${id}-play-button`}
          onClick={toggle}
          aria-label={playing ? `Pause ${title}` : `Play ${title}`}
          className="h-9 w-9 shrink-0 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-colors duration-300"
        >
          {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
        </button>
        <div className="min-w-0">
          <p className="font-display text-base tracking-wider text-white uppercase leading-tight">
            <span className="text-red-600">CRAYFOX</span> — {title}
          </p>
          <p className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-zinc-500 mt-0.5">
            {fadeInLabel} {mmss(start)} · {fadeOutLabel} {mmss(end)}
          </p>
        </div>
      </div>
      <div className="h-1 bg-zinc-800 w-full">
        <div
          data-testid={`audio-teaser-${id}-progress`}
          className="h-1 bg-red-600 transition-[width] duration-300"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
};

const TEASERS = [
  { id: "lime-tree", title: "The Lime Tree", src: "/the-lime-tree.mp3", start: 30, end: 75 },
  { id: "song-of-the-rain", title: "Song of the Rain", src: "/song-of-the-rain.mp3", start: 57, end: 153 },
  { id: "ancor", title: "Ancor", src: "/ancor.mp3", start: 120, end: 177 },
  { id: "unreal", title: "Unreal", src: "/unreal.mp3", start: 2, end: 130 },
  { id: "hell-knell", title: "Hell Knell", src: "/hell-knell.mp3", start: 154, end: 208 },
  { id: "park", title: "Park", src: "/park.mp3", start: 129, end: 197 },
];

const AudioTeasers = () => {
  const [playingId, setPlayingId] = useState(null);
  const { t } = useLang();
  return (
    <div className="grid grid-cols-2 gap-3 mt-4">
      {TEASERS.map((teaser) => (
        <AudioTeaser
          key={teaser.id}
          {...teaser}
          playingId={playingId}
          onToggle={setPlayingId}
          fadeInLabel={t.hero.fadeIn}
          fadeOutLabel={t.hero.fadeOut}
        />
      ))}
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const { t } = useLang();

  const lines = [
    { text: "CRAYFOX", cls: "text-3d-brand", big: true },
    { text: "NO LIMITS. NO SILENCE.", cls: "text-red-600" },
    { text: "UNFORGIVING ENERGY.", cls: "text-outline" },
  ];

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[15%] h-[130%]">
        <img
          src={ASSETS.livePyro}
          alt="CRAYFOX live on stage"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/70 to-[#0A0A0C]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C]/80 via-transparent to-transparent" />
      </motion.div>

      <CornerTicks className="border-red-600/40 hidden sm:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 pt-32 w-full grid gap-10 lg:gap-14 lg:grid-cols-[1.15fr_1fr] lg:[grid-template-areas:'text_media'_'cta_media']">
        <div className="lg:[grid-area:text] self-end">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono-tech text-xs sm:text-sm tracking-[0.35em] text-zinc-300 uppercase mb-6 flex items-center gap-3"
          >
            <span className="rec-dot inline-block h-2 w-2 rounded-full bg-red-600" />
            {t.hero.kicker}
          </motion.p>

          <h1 data-testid="hero-headline" aria-label="CRAYFOX — No Limits. No Silence. Unforgiving Energy.">
            <MaskedImage src="/crayfox-wordmark.png" alt="CRAYFOX" delay={0.15} />
            <MaskedLine text={lines[1].text} cls={lines[1].cls} delay={0.3} small spaced />
            <MaskedLine text={lines[2].text} cls={lines[2].cls} delay={0.45} small />
          </h1>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative lg:[grid-area:media]"
        >
          <CornerTicks className="border-red-600/60" />
          <video
            data-testid="hero-promo-video"
            autoPlay
            muted
            loop
            playsInline
            controls
            className="w-full aspect-video object-cover bg-black border border-zinc-800 shadow-[0_8px_40px_rgba(0,0,0,0.8)]"
          >
            <source src="/promo.webm" type="video/webm" />
            <source src="/promo.mp4" type="video/mp4" />
          </video>
          <p className="mt-3 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-2">
            <span className="rec-dot inline-block h-1.5 w-1.5 rounded-full bg-red-600" />
            {t.hero.videoCaption}
          </p>
          <AudioTeasers />
        </motion.div>

        <div className="lg:[grid-area:cta] self-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8 }}
            className="max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed"
          >
            {t.hero.para}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-button"
              onClick={() => scrollToId("booking")}
              className="font-mono-tech text-sm tracking-[0.25em] uppercase bg-red-600 hover:bg-red-700 text-white px-8 py-4 transition-colors duration-300 shadow-[0_0_30px_rgba(220,38,38,0.35)]"
            >
              {t.hero.book}
            </button>
            <button
              data-testid="hero-rider-button"
              onClick={() => scrollToId("rider")}
              className="font-mono-tech text-sm tracking-[0.25em] uppercase border border-zinc-600 hover:border-red-600 hover:text-red-500 text-zinc-200 px-8 py-4 transition-colors duration-300"
            >
              {t.hero.rider}
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
